// AIDEV-NOTE: SPA-scoped Radix Dialog wrapper using tw-spa-* theme tokens.
// Follows the Drawer/Popover Object.assign composition pattern.
// Content renders its own Portal+Overlay internally for simple consumer API.

// AIDEV-NOTE: `radix-dialog-next` is an alias for @radix-ui/react-dialog 1.1.16, installed alongside
// the repo-wide 1.0.5 so this one dialog can be upgraded without moving every consumer at once.
// 1.0.5 restores body.pointerEvents only when its layer Set is size 1 and deletes from that Set in a
// separate effect, so a missed restore leaves the whole page pointer-events:none — the page stops
// taking clicks and a long-press finds no selectable text under the finger. 1.1.12+ of the
// underlying react-dismissable-layer deletes first and restores when the Set is empty. Migrate the
// other two consumers (common/InteractiveModal, Drawing/ShortcutsModal) and drop the alias.
import * as DialogPrimitive from 'radix-dialog-next';
import React from 'react';
import { tv } from 'tailwind-variants';
import cn from '@/utils/cn';
import Icon from '@/components/ui/Icon';
import SPAButton from './SPAButton';
import { useSPAThemeScope } from './SPAThemeScope';

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'tw-fixed tw-inset-0 tw-z-[1050] tw-bg-gray-950/60 tw-backdrop-blur-[2px]',
      'data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0',
      className,
    )}
    {...props}
  />
));

DialogOverlay.displayName = DialogPrimitive.Overlay.displayName;

const DialogPortal = DialogPrimitive.Portal;

const contentVariants = tv({
  base: [
    'tw-fixed tw-left-1/2 tw--translate-x-1/2',
    'tw-z-[1051] tw-w-[calc(100vw-2rem)] tw-max-h-[85vh] tw-box-border',
    'tw-bg-spa-bg-secondary tw-border tw-border-solid tw-border-spa-border',
    'tw-rounded-xl tw-flex tw-flex-col tw-overflow-hidden tw-outline-none',
    'tw-shadow-[0_1.5rem_3.5rem_-0.75rem_rgba(0,0,0,0.4),0_0.5rem_1.25rem_-0.5rem_rgba(0,0,0,0.25)]',
    // AIDEV-NOTE: The slide-* classes are not decoration — they are what keeps the dialog centred
    // while it animates. tailwindcss-animate builds one keyframe whose `from` transform is composed
    // entirely from --tw-enter-*, so without an enter translate it starts at translate3d(0,0,0) and
    // travels to the -50%/-50% centring, reading as a flash that snaps into place.
    //
    // Each axis is held at its resting offset (the y axis lives in the placement variant), so the
    // dialog does not travel at all — it fades and settles from 98% about its own centre. (shadcn's
    // stock -48% on the y axis adds a rise, which reads as entering from a direction rather than
    // from nowhere.)
    //
    // There is deliberately NO data-[state=closed] exit animation. Radix Presence keeps the node
    // mounted until an animationend fires on it whose name still matches the computed animation-name;
    // if that event never arrives the dialog never unmounts, and its scroll lock, aria-hidden and
    // body pointer-events:none stay applied — the page is then unscrollable and untappable. iOS
    // Safari is where this actually bites. With no exit animation the computed name is 'none',
    // Presence unmounts synchronously, and close can never strand the page.
    'data-[state=open]:tw-duration-200',
    'data-[state=open]:tw-animate-in data-[state=open]:tw-fade-in-0 data-[state=open]:tw-zoom-in-[0.98]',
    'data-[state=open]:tw-slide-in-from-left-1/2',
  ],
  defaultVariants: { placement: 'center', size: 'md' },
  variants: {
    // `top` pins the header a little below the top edge, so a dialog whose body loads after it
    // opens (the resource editor) grows downward instead of re-centring with a jump.
    placement: {
      center: 'tw-top-1/2 tw--translate-y-1/2 data-[state=open]:tw-slide-in-from-top-1/2',
      top: 'tw-top-[6vh]',
    },
    size: {
      lg: 'tw-max-w-4xl',
      md: 'tw-max-w-2xl',
      sm: 'tw-max-w-md',
      xl: 'tw-max-w-6xl',
    },
  },
});

type DialogPlacement = 'center' | 'top';
type DialogSize = 'sm' | 'md' | 'lg' | 'xl';
type DialogContentRef = React.ElementRef<typeof DialogPrimitive.Content>;
type DialogContentOptions = { placement?: DialogPlacement; size?: DialogSize };
type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & DialogContentOptions;

const DialogContent = React.forwardRef<DialogContentRef, DialogContentProps>((
  { className, children, placement, size, ...props },
  ref,
) => {
  // Portalled into document.body, so a legacy mount's pinned theme class has to travel with it.
  const themeScope = useSPAThemeScope();

  return (
    <DialogPortal>
      <DialogOverlay />
      <DialogPrimitive.Content
        ref={ref}
        className={cn(contentVariants({ placement, size }), themeScope, className)}
        {...props}
      >
        {children}
      </DialogPrimitive.Content>
    </DialogPortal>
  );
});

DialogContent.displayName = DialogPrimitive.Content.displayName;

// AIDEV-NOTE: tw-pr-12 reserves the CloseButton's absolutely-positioned corner so long titles wrap
// before they reach it.
const DialogHeader = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-px-4 tw-pt-3.5 tw-pb-3 tw-pr-12 tw-relative',
      className,
    )}
    {...props}
  />
);

DialogHeader.displayName = 'SPADialog.Header';

const DialogBody = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-px-4 tw-py-4 tw-flex-1 tw-overflow-y-auto tw-text-spa-primary',
      className,
    )}
    {...props}
  />
);

DialogBody.displayName = 'SPADialog.Body';

const DialogFooter = ({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'tw-mt-auto tw-px-4 tw-py-2.5',
      'tw-flex tw-items-center tw-justify-end tw-gap-2',
      className,
    )}
    {...props}
  />
);

DialogFooter.displayName = 'SPADialog.Footer';

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn(
      'tw-text-base tw-font-semibold tw-leading-6 tw-text-spa-heading tw-m-0 tw-pb-0',
      className,
    )}
    {...props}
  />
));

DialogTitle.displayName = DialogPrimitive.Title.displayName;

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('tw-text-sm tw-leading-5 tw-text-spa-primary tw-m-0', className)}
    {...props}
  />
));

DialogDescription.displayName = DialogPrimitive.Description.displayName;

const DialogClose = DialogPrimitive.Close;

const focusRing = [
  'focus-visible:tw-outline-none focus-visible:tw-ring-2 focus-visible:tw-ring-spa-accent',
  'focus-visible:tw-ring-offset-2 focus-visible:tw-ring-offset-spa-bg-secondary',
];

// AIDEV-NOTE: Absolutely positioned against Header's padding box, which spans the full content
// width — Header's own tw-px does NOT move it. Keep tw-right-* equal to the dialog's shared inset.
// The negative margin is optical alignment: it pulls the box out by the icon's centering padding
// — half of (tw-w-7 minus the icon) — so the glyph, not the hit area, lands on that inset.
const DialogCloseButton = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Close>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Close>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Close
    ref={ref}
    aria-label="Close popup"
    className={cn(
      'tw-absolute tw-top-3 tw-right-4 tw--mr-1.5 tw-h-7 tw-w-7 tw-p-0 tw-box-border',
      'tw-flex tw-items-center tw-justify-center tw-rounded-md',
      'tw-bg-transparent tw-border-none tw-cursor-pointer tw-transition-colors',
      'tw-text-spa-tertiary hover:tw-bg-spa-bg-hover hover:tw-text-spa-heading',
      focusRing,
      className,
    )}
    type="button"
    {...props}
  >
    <Icon icon="Close" size={16} />
  </DialogPrimitive.Close>
));

DialogCloseButton.displayName = 'SPADialog.CloseButton';

const DialogTrigger = DialogPrimitive.Trigger;

// AIDEV-NOTE: Compose onto a local wrapper, NEVER Object.assign(DialogPrimitive.Root, ...) —
// mutating the shared radix export collides with any other composite built on the same primitive
// (see SPAPopover for the popover version of this bug).
const SPADialogRoot = (props: React.ComponentProps<typeof DialogPrimitive.Root>) => (
  <DialogPrimitive.Root {...props} />
);

const SPADialog = Object.assign(SPADialogRoot, {
  Body: DialogBody,
  Button: SPAButton,
  Close: DialogClose,
  CloseButton: DialogCloseButton,
  Content: DialogContent,
  Description: DialogDescription,
  Footer: DialogFooter,
  Header: DialogHeader,
  Overlay: DialogOverlay,
  Portal: DialogPortal,
  Title: DialogTitle,
  Trigger: DialogTrigger,
});

export default SPADialog;
